import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select, { components as selectComponents } from 'react-select';
import moment from 'moment';
import 'rc-calendar/assets/index.css';

import PageHeader from '../../../components/common/PageHeader';
import EmptyData from '../../../components/UI/EmptyData';
import { TableSkeleton } from '../../../components/UI/Skeleton/Skeleton';
import TableCard from '../../../components/UI/TableToolbar/TableCard';
import TableToolbar from '../../../components/UI/TableToolbar/TableToolbar';
import RowActionMenu from '../../../components/UI/RowActionMenu/RowActionMenu';
import DatePicker from '../../../components/UI/DatePicker';
import {
  useBulkReviewTimeEditRequestsMutation,
  useGetTimeEditRequestsQuery,
  useReviewTimeEditRequestMutation
} from '../../../redux/apis/timeTrackingApi';
import { useIsLoggedInQuery } from '../../../redux/apis/authApi';
import { useGetEmployeeDropDownQuery } from '../../../redux/apis/employee';
import { selectApiLocality, selectDisplayTimezone } from '../../../redux/utils/apiLocality';
import { ADMIN_HR_ROLES } from '../../../utils/constant/global';
import {
  buildTimedoctorRange,
  formatClockInTimezone,
  toLocalDateKey
} from '../../../utils/timedoctorAttendanceHelpers';
import { notification } from '../../../utils/notification';
import { TOAST_TYPE } from '../../../utils/constant/errorMessage';
import { sharedTableStyles } from '../../../utils/tableStyles';
import { skipRow } from '../../../utils/handler';

import './TimeEditRequestList.scss';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' }
];

const filterSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 100050 }),
  control: (base) => ({
    ...base,
    minHeight: 42,
    height: 42,
    borderRadius: 11,
    borderColor: '#e9edf3',
    backgroundColor: '#f8fafc',
    boxShadow: 'none',
    '&:hover': { borderColor: '#e9edf3' }
  }),
  valueContainer: (base) => ({ ...base, height: 40, padding: '0 10px' }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  indicatorSeparator: () => ({ display: 'none' }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontWeight: 600, fontSize: 13 }),
  singleValue: (base) => ({ ...base, fontWeight: 600, fontSize: 13, color: '#1f2937' })
};

const statusBadgeClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'ter-status ter-status--approved';
  if (s === 'rejected') return 'ter-status ter-status--rejected';
  if (s === 'pending') return 'ter-status ter-status--pending';
  return 'ter-status';
};

const formatDuration = (startTs, endTs) => {
  const s = Number(startTs);
  const e = Number(endTs);
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
  const totalSeconds = Math.floor((e - s) / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (sec > 0 || (h === 0 && m === 0)) parts.push(`${sec}s`);
  return parts.join(' ');
};

const getRequestId = (row) => row?._id || row?.id;

const canApproveRequest = (row) => {
  const status = String(row?.status || '').toLowerCase();
  return status === 'pending' || status === 'rejected';
};

const canRejectRequest = (row) => {
  const status = String(row?.status || '').toLowerCase();
  return status === 'pending' || status === 'approved';
};

const pluralizeRequests = (count) => `${count} request${count === 1 ? '' : 's'}`;

const extractRequests = (res) => {
  const candidates = [
    res?.data?.data,
    res?.data?.docs,
    res?.data?.result,
    res?.data?.data?.docs,
    res?.data?.data?.result,
    res?.data,
    res?.docs,
    res?.result
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

const TimeEditRequestList = () => {
  const navigate = useNavigate();
  const apiLocality = useSelector(selectApiLocality);
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';
  const { data: authData } = useIsLoggedInQuery();
  const roleName = authData?.data?.hrxRole?.roleName;
  const isAdminOrHr = ADMIN_HR_ROLES.includes(roleName);

  const isAuLocality = useMemo(
    () => (Array.isArray(apiLocality) ? apiLocality : []).includes('AU'),
    [apiLocality]
  );

  const [statusFilter, setStatusFilter] = useState(
    isAdminOrHr ? STATUS_OPTIONS[1] : STATUS_OPTIONS[0]
  );
  const [userFilter, setUserFilter] = useState(null);
  const [selectType, setSelectType] = useState({
    type: 'Day',
    currentDate: moment()
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [reviewModal, setReviewModal] = useState({
    open: false,
    row: null,
    bulkIds: null,
    status: '',
    note: ''
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectionToggle, setClearSelectionToggle] = useState(false);

  const clearSelection = () => {
    setSelectedRows([]);
    setClearSelectionToggle((prev) => !prev);
  };

  const dateRange = useMemo(() => {
    const { from, to } = buildTimedoctorRange(
      selectType.currentDate || moment(),
      timeZone,
      selectType.type || 'Day'
    );
    const fromMs = Date.parse(from);
    const toMs = Date.parse(to);
    return {
      from: Number.isFinite(fromMs) ? fromMs : undefined,
      to: Number.isFinite(toMs) ? toMs : undefined
    };
  }, [selectType.currentDate, selectType.type, timeZone]);

  const handlePeriodChange = (type) => {
    const value = selectType.currentDate || moment();
    setSelectType({ type, currentDate: value });
    setPage(1);
  };

  const handleDateChange = (value) => {
    setSelectType((prev) => ({
      ...prev,
      currentDate: value || moment()
    }));
    setPage(1);
  };

  const {
    data: employeeDropdown,
    isFetching: isEmployeesLoading,
    refetch: employeeDropDownQuery
  } = useGetEmployeeDropDownQuery(
    {
      skip: skipRow(employeePage, 50),
      limit: 50,
      search: employeeSearch,
      sort: 'name',
      sortType: 'asc'
    },
    { skip: !isAdminOrHr || isAuLocality }
  );

  useEffect(() => {
    if (!Array.isArray(employeeDropdown?.data)) return;
    setEmployeeOptions((prev) => {
      const next = employeePage === 1 ? [] : [...prev];
      const map = new Map(next.map((o) => [String(o.value), o]));
      employeeDropdown.data.forEach((emp) => {
        const id = emp?._id || emp?.id;
        if (!id || map.has(String(id))) return;
        map.set(String(id), { value: id, label: emp?.name || emp?.userName || '—' });
      });
      return Array.from(map.values());
    });
  }, [employeeDropdown, employeePage]);

  const employeeDropdownEnabled = isAdminOrHr && !isAuLocality;

  // RTK refetch throws if the query was never started; never let that crash the page
  const safeRefetchEmployees = () => {
    try {
      employeeDropDownQuery?.();
    } catch (err) {
      console.error('Employee dropdown refetch failed:', err);
    }
  };

  // Refetch after the page/search arg has updated so every scroll hits the API
  const employeeFirstRunRef = useRef(true);
  useEffect(() => {
    if (employeeFirstRunRef.current) {
      employeeFirstRunRef.current = false;
      return;
    }
    if (employeeDropdownEnabled) safeRefetchEmployees();
  }, [employeePage, employeeSearch]);

  /** Returns true when the next page was requested. */
  const handleEmployeeScrollToBottom = () => {
    const pageRows = Array.isArray(employeeDropdown?.data) ? employeeDropdown.data.length : 0;
    // A full page also counts as "more", in case isNext is missing or wrong
    const hasMore = Boolean(employeeDropdown?.isNext) || pageRows >= 50;
    if (!hasMore || isEmployeesLoading) return false;
    setEmployeePage((p) => p + 1);
    return true;
  };

  // react-select's onMenuScrollToBottom only reacts to wheel/touch, so also listen to the
  // list's native scroll. The ref keeps the memoized component fresh.
  const employeeScrollRef = useRef(handleEmployeeScrollToBottom);
  employeeScrollRef.current = handleEmployeeScrollToBottom;
  const employeeMenuListComponents = useMemo(() => {
    let armed = true;
    const EmployeeMenuList = (props) => (
      <selectComponents.MenuList
        {...props}
        innerProps={{
          ...props.innerProps,
          onScroll: (e) => {
            const el = e?.currentTarget;
            if (!el) return;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
            if (!nearBottom) {
              armed = true;
            } else if (armed) {
              armed = !employeeScrollRef.current?.();
            }
          }
        }}
      />
    );
    return { MenuList: EmployeeMenuList };
  }, []);

  // Employee search is server-side: debounce typing, then reset the paged list
  const employeeSearchTimerRef = useRef(null);
  const appliedEmployeeSearchRef = useRef('');
  useEffect(
    () => () => {
      if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current);
    },
    []
  );
  const applyEmployeeSearch = (term) => {
    if (appliedEmployeeSearchRef.current === term) return;
    appliedEmployeeSearchRef.current = term;
    setEmployeeSearch(term);
    setEmployeePage(1);
  };
  const handleEmployeeSearchInput = (text, meta) => {
    if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current);
    if (meta?.action !== 'input-change') {
      // Selected / menu closed / blurred: drop the search so the full list comes back
      applyEmployeeSearch('');
      return;
    }
    employeeSearchTimerRef.current = setTimeout(() => {
      applyEmployeeSearch(String(text || '').trim());
    }, 400);
  };

  const queryArgs = useMemo(() => {
    const params = { page, limit: rowsPerPage };
    if (statusFilter?.value) params.status = statusFilter.value;
    if (isAdminOrHr && userFilter?.value) params.userId = userFilter.value;
    // Day / Week / Month → from & to (ms), same as Time Tracking
    if (dateRange.from != null) params.from = dateRange.from;
    if (dateRange.to != null) params.to = dateRange.to;
    return params;
  }, [page, rowsPerPage, statusFilter, userFilter, dateRange, isAdminOrHr]);

  const { data, isLoading, isFetching, refetch } = useGetTimeEditRequestsQuery(queryArgs, {
    skip: isAuLocality || dateRange.from == null,
    refetchOnMountOrArgChange: true
  });

  const [reviewRequest, { isLoading: isSingleReviewing }] = useReviewTimeEditRequestMutation();
  const [bulkReviewRequests, { isLoading: isBulkReviewing }] =
    useBulkReviewTimeEditRequestsMutation();
  const isReviewing = isSingleReviewing || isBulkReviewing;

  useEffect(() => {
    setSelectedRows([]);
    setClearSelectionToggle((prev) => !prev);
  }, [queryArgs]);

  const bulkApproveIds = useMemo(
    () => selectedRows.filter(canApproveRequest).map(getRequestId).filter(Boolean),
    [selectedRows]
  );
  const bulkRejectIds = useMemo(
    () => selectedRows.filter(canRejectRequest).map(getRequestId).filter(Boolean),
    [selectedRows]
  );

  const selectedRowStyles = useMemo(() => {
    const ids = new Set(selectedRows.map(getRequestId));
    return [
      {
        when: (row) => ids.has(getRequestId(row)),
        style: {
          backgroundColor: '#f5f7ff',
          boxShadow: 'inset 3px 0 0 #484c7f'
        }
      }
    ];
  }, [selectedRows]);

  const openBulkReview = (status) => {
    const bulkIds = status === 'Approved' ? bulkApproveIds : bulkRejectIds;
    if (!bulkIds.length) return;
    setReviewModal({ open: true, row: null, bulkIds, status, note: '' });
  };

  const rows = useMemo(() => extractRequests(data), [data]);
  const totalRows =
    data?.data?.pagination?.totalRecords ||
    data?.data?.totalDocs ||
    data?.data?.total ||
    data?.totalDocs ||
    rows.length;

  const openReview = (row, status) => {
    setReviewModal({ open: true, row, bulkIds: null, status, note: '' });
  };

  const closeReview = () => {
    setReviewModal({ open: false, row: null, bulkIds: null, status: '', note: '' });
  };

  const isBulkReview = Array.isArray(reviewModal.bulkIds);

  /** Open Time Tracking for this request's employee + day */
  const openTimelogForRequest = (row) => {
    if (!row) return;
    const userRaw = row?.userId ?? row?.requestedBy ?? row?.user;
    const userId =
      typeof userRaw === 'object' && userRaw != null ? userRaw?._id || userRaw?.id : userRaw;
    const userName =
      (typeof userRaw === 'object' && userRaw != null
        ? userRaw?.name || userRaw?.userName
        : null) ||
      row?.userName ||
      '';

    let dateYmd = null;
    const dateTs = Number(row?.date ?? row?.requestedStart ?? row?.start);
    if (Number.isFinite(dateTs) && dateTs > 0) {
      dateYmd = toLocalDateKey(dateTs, timeZone) || moment(dateTs).format('YYYY-MM-DD');
    } else if (row?.date) {
      const m = moment(row.date);
      if (m.isValid()) dateYmd = m.format('YYYY-MM-DD');
    }

    const qs = new URLSearchParams();
    if (userId) qs.set('userId', String(userId));
    if (userName) qs.set('userName', String(userName));
    if (dateYmd) qs.set('date', dateYmd);
    navigate(`/attendance-time-tracking?${qs.toString()}`);
  };

  const handleBulkReviewSave = async () => {
    const { bulkIds, status } = reviewModal;
    if (!bulkIds?.length || !status) return;

    try {
      const res = await bulkReviewRequests({
        requestIds: bulkIds,
        status,
        reviewNote: String(reviewModal.note || '').trim()
      }).unwrap();

      const succeeded = Array.isArray(res?.data?.succeeded) ? res.data.succeeded : null;
      const failed = Array.isArray(res?.data?.failed) ? res.data.failed : [];
      const verb = status === 'Approved' ? 'approved' : 'rejected';

      if (succeeded == null) {
        notification(
          res?.statusMessage || `${pluralizeRequests(bulkIds.length)} ${verb}.`,
          TOAST_SUCCESS
        );
      } else if (succeeded.length) {
        notification(`${pluralizeRequests(succeeded.length)} ${verb}.`, TOAST_SUCCESS);
      }
      if (failed.length) {
        const reason = failed[0]?.message ? ` ${failed[0].message}.` : '';
        notification(`Failed to update ${pluralizeRequests(failed.length)}.${reason}`, TOAST_ERROR);
      }

      closeReview();
      clearSelection();
      refetch();
    } catch (err) {
      notification(
        err?.data?.statusMessage ||
          err?.data?.message ||
          err?.data?.data?.message ||
          'Failed to review requests.',
        TOAST_ERROR
      );
    }
  };

  const handleReviewSave = async () => {
    if (isBulkReview) {
      handleBulkReviewSave();
      return;
    }
    const id = reviewModal.row?._id || reviewModal.row?.id;
    if (!id || !reviewModal.status) return;

    try {
      const res = await reviewRequest({
        id,
        status: reviewModal.status,
        reviewNote: String(reviewModal.note || '').trim()
      }).unwrap();
      notification(
        res?.statusMessage ||
          (reviewModal.status === 'Approved'
            ? 'Request approved — time log created.'
            : 'Request rejected.'),
        TOAST_SUCCESS
      );
      closeReview();
      refetch();
    } catch (err) {
      notification(
        err?.data?.statusMessage ||
          err?.data?.message ||
          err?.data?.data?.message ||
          'Failed to review request.',
        TOAST_ERROR
      );
    }
  };

  const columns = useMemo(() => {
    const cols = [
      {
        name: 'EMPLOYEE',
        omit: !isAdminOrHr,
        grow: 1.3,
        cell: (row) => (
          <span className="fw-bold">
            {row?.userId?.name || row?.requestedBy?.name || row?.user?.name || row?.userName || '—'}
          </span>
        )
      },
      {
        name: 'DATE',
        width: '120px',
        cell: (row) => <span>{row?.date ? moment(row.date).format('DD MMM YYYY') : '—'}</span>
      },
      {
        name: 'PROJECT',
        grow: 1.2,
        cell: (row) => <span>{row?.projectName || row?.project?.name || '—'}</span>
      },
      {
        name: 'TASK',
        grow: 1.3,
        cell: (row) => <span>{row?.taskName || '—'}</span>
      },
      {
        name: 'START',
        width: '100px',
        cell: (row) => {
          const ts = row?.requestedStart ?? row?.start;
          return (
            <span className="text-nowrap">{ts ? formatClockInTimezone(ts, timeZone) : '—'}</span>
          );
        }
      },
      {
        name: 'END',
        width: '100px',
        cell: (row) => {
          const ts = row?.requestedEnd ?? row?.end;
          return (
            <span className="text-nowrap">{ts ? formatClockInTimezone(ts, timeZone) : '—'}</span>
          );
        }
      },
      {
        name: 'DURATION',
        width: '100px',
        cell: (row) => {
          const label = formatDuration(
            row?.requestedStart ?? row?.start,
            row?.requestedEnd ?? row?.end
          );
          return <span className="fw-semibold text-nowrap">{label || '—'}</span>;
        }
      },
      {
        name: 'REASON',
        width: '150px',
        cell: (row) => {
          const reason = row?.reason;
          if (!reason) return <span className="text-muted">—</span>;
          return (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`reason-tip-${row?._id || ''}`}>{reason}</Tooltip>}
            >
              <span className="ter-reason">
                <i className="icofont-info-circle ter-reason__icon" aria-hidden />
                <span className="ter-reason__text">{reason}</span>
              </span>
            </OverlayTrigger>
          );
        }
      },
      {
        name: 'STATUS',
        width: '120px',
        cell: (row) => <span className={statusBadgeClass(row?.status)}>{row?.status || '—'}</span>
      },
      {
        name: 'SUBMITTED',
        width: '150px',
        cell: (row) => (
          <span className="text-nowrap">
            {row?.createdAt ? moment(row.createdAt).format('DD MMM YYYY HH:mm') : '—'}
          </span>
        )
      }
    ];

    if (isAdminOrHr) {
      cols.push({
        name: 'ACTION',
        width: '90px',
        ignoreRowClick: true,
        cell: (row) => {
          const canApprove = canApproveRequest(row);
          const canReject = canRejectRequest(row);
          if (!canApprove && !canReject) {
            return <span className="text-muted">—</span>;
          }
          const actions = [];
          if (canApprove) {
            actions.push({
              label: 'Approve',
              icon: 'icofont-check-circled',
              variant: 'success',
              onClick: () => openReview(row, 'Approved')
            });
          }
          if (canReject) {
            actions.push({
              label: 'Reject',
              icon: 'icofont-close-circled',
              variant: 'danger',
              onClick: () => openReview(row, 'Rejected')
            });
          }
          return <RowActionMenu actions={actions} />;
        }
      });
    }

    return cols;
  }, [isAdminOrHr, timeZone]);

  if (isAuLocality) {
    return (
      <div className="container-xxl">
        <PageHeader headerTitle="Time Edit Req" />
        <TableCard>
          <div className="p-4 text-muted mb-0">
            Time edit requests apply to IN locality only. AU users edit time directly.
          </div>
        </TableCard>
      </div>
    );
  }

  return (
    <div className="container-xxl ter-page">
      <PageHeader headerTitle="Time Edit Req" />

      <div className="row clearfix g-3">
        <div className="col-sm-12">
          <TableCard>
            <TableToolbar
              showSearch={false}
              filters={
                <>
                  {isAdminOrHr ? (
                    <div className="ter-filter ter-filter--employee">
                      <Select
                        classNamePrefix="ter-select"
                        options={employeeOptions}
                        value={userFilter}
                        isClearable
                        isLoading={isEmployeesLoading}
                        filterOption={null}
                        onInputChange={handleEmployeeSearchInput}
                        components={employeeMenuListComponents}
                        placeholder="All employees"
                        onChange={(opt) => {
                          setUserFilter(opt);
                          setPage(1);
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={filterSelectStyles}
                      />
                    </div>
                  ) : null}

                  <div className="ter-filter ter-filter--period">
                    <div className="ter-period-tabs" role="tablist" aria-label="Time period">
                      {['Day', 'Week', 'Month'].map((period) => (
                        <button
                          key={period}
                          type="button"
                          role="tab"
                          aria-selected={selectType.type === period}
                          className={`ter-period-tab${
                            selectType.type === period ? ' ter-period-tab--active' : ''
                          }`}
                          onClick={() => handlePeriodChange(period)}
                        >
                          {period}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="ter-filter ter-filter--date">
                    <div
                      className={`ter-period-date ter-period-date--${String(
                        selectType.type || 'Day'
                      ).toLowerCase()}`}
                    >
                      <span className="ter-period-date__icon" aria-hidden>
                        <i className="bi bi-calendar3" />
                      </span>
                      <div className="ter-period-date__picker">
                        <DatePicker
                          initialDate={selectType.currentDate}
                          type={selectType.type}
                          handleChangeDate={handleDateChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="ter-filter ter-filter--status">
                    <Select
                      classNamePrefix="ter-select"
                      options={STATUS_OPTIONS}
                      value={statusFilter}
                      onChange={(opt) => {
                        setStatusFilter(opt);
                        setPage(1);
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={filterSelectStyles}
                    />
                  </div>
                </>
              }
            />

            {isAdminOrHr && selectedRows.length ? (
              <div className="ter-bulk-bar">
                <div className="ter-bulk-bar__info">
                  <span className="ter-bulk-bar__count">{selectedRows.length}</span>
                  <span className="ter-bulk-bar__text">
                    <span className="ter-bulk-bar__title">
                      {pluralizeRequests(selectedRows.length)} selected
                    </span>
                    <span className="ter-bulk-bar__hint">
                      {bulkApproveIds.length} can be approved · {bulkRejectIds.length} can be
                      rejected
                    </span>
                  </span>
                </div>
                <div className="ter-bulk-bar__actions">
                  <button
                    type="button"
                    className="ter-bulk-bar__btn ter-bulk-bar__btn--ghost"
                    onClick={clearSelection}
                    disabled={isReviewing}
                  >
                    <i className="bi bi-x-lg" aria-hidden />
                    Clear
                  </button>
                  <button
                    type="button"
                    className="ter-bulk-bar__btn ter-bulk-bar__btn--reject"
                    onClick={() => openBulkReview('Rejected')}
                    disabled={isReviewing || !bulkRejectIds.length}
                  >
                    <i className="icofont-close-circled" aria-hidden />
                    Reject
                    <span className="ter-bulk-bar__badge">{bulkRejectIds.length}</span>
                  </button>
                  <button
                    type="button"
                    className="ter-bulk-bar__btn ter-bulk-bar__btn--approve"
                    onClick={() => openBulkReview('Approved')}
                    disabled={isReviewing || !bulkApproveIds.length}
                  >
                    <i className="icofont-check-circled" aria-hidden />
                    Approve
                    <span className="ter-bulk-bar__badge">{bulkApproveIds.length}</span>
                  </button>
                </div>
              </div>
            ) : null}

            <div className="card-body">
              <DataTable
                columns={columns}
                data={rows}
                customStyles={sharedTableStyles}
                className="table myDataTable ter-table table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
                highlightOnHover
                pointerOnHover
                selectableRows={isAdminOrHr}
                selectableRowDisabled={(row) => !canApproveRequest(row) && !canRejectRequest(row)}
                conditionalRowStyles={selectedRowStyles}
                onSelectedRowsChange={({ selectedRows: next }) => setSelectedRows(next)}
                clearSelectedRows={clearSelectionToggle}
                onRowClicked={openTimelogForRequest}
                pagination
                paginationServer
                paginationPerPage={rowsPerPage}
                paginationDefaultPage={page}
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                onChangePage={setPage}
                onChangeRowsPerPage={(next) => {
                  setRowsPerPage(next);
                  setPage(1);
                }}
                paginationTotalRows={totalRows}
                noDataComponent={<EmptyData />}
                progressPending={isLoading || isFetching}
                progressComponent={<TableSkeleton columns={isAdminOrHr ? 10 : 8} />}
              />
            </div>
          </TableCard>
        </div>
      </div>

      <Modal
        centered
        className="ter-review-modal"
        dialogClassName="ter-review-modal__dialog"
        contentClassName="ter-review-modal__content"
        show={reviewModal.open}
        onHide={closeReview}
      >
        <Modal.Header className="ter-review-modal__header">
          <div className="ter-review-modal__header-content">
            <span className="ter-review-modal__header-icon" aria-hidden="true">
              <i
                className={
                  reviewModal.status === 'Approved'
                    ? 'icofont-check-circled'
                    : 'icofont-close-circled'
                }
              />
            </span>
            <div className="ter-review-modal__header-copy">
              <Modal.Title className="fw-bold ter-review-modal__title">
                {isBulkReview
                  ? `${reviewModal.status === 'Approved' ? 'Approve' : 'Reject'} ${pluralizeRequests(
                      reviewModal.bulkIds.length
                    )}`
                  : reviewModal.status === 'Approved'
                    ? 'Approve request'
                    : 'Reject request'}
              </Modal.Title>
              <p className="ter-review-modal__subtitle">
                {reviewModal.status === 'Approved'
                  ? isBulkReview
                    ? 'On approve, a new PMS time log is created for each selected request.'
                    : 'On approve, a new PMS time log is created for this request.'
                  : 'Rejected requests do not create a time log.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close ter-review-modal__close"
            aria-label="Close"
            onClick={closeReview}
          />
        </Modal.Header>
        <Modal.Body className="ter-review-modal__body">
          <div className="ter-review-modal__section">
            {isBulkReview && selectedRows.length > reviewModal.bulkIds.length ? (
              <p className="ter-review-modal__skip">
                <i className="bi bi-info-circle" aria-hidden />
                {pluralizeRequests(selectedRows.length - reviewModal.bulkIds.length)} already{' '}
                {reviewModal.status === 'Approved' ? 'approved' : 'rejected'} will be skipped.
              </p>
            ) : null}
            <label className="ter-review-modal__label" htmlFor="ter-review-note">
              Review note (optional)
            </label>
            <textarea
              id="ter-review-note"
              className="form-control ter-review-modal__textarea"
              rows={3}
              maxLength={500}
              value={reviewModal.note}
              onChange={(e) => setReviewModal((prev) => ({ ...prev, note: e.target.value }))}
              placeholder="Add a note for the employee..."
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="ter-review-modal__footer">
          <button type="button" className="btn ter-review-modal__cancel" onClick={closeReview}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ter-review-modal__submit${
              reviewModal.status === 'Approved' ? ' ter-review-modal__submit--approve' : ''
            }`}
            disabled={isReviewing}
            onClick={handleReviewSave}
          >
            {isReviewing ? (
              'Saving...'
            ) : (
              <>
                <i
                  className={
                    reviewModal.status === 'Approved'
                      ? 'icofont-check-circled'
                      : 'icofont-close-circled'
                  }
                  aria-hidden="true"
                />
                {`${reviewModal.status === 'Approved' ? 'Approve' : 'Reject'}${
                  isBulkReview ? ` (${reviewModal.bulkIds.length})` : ''
                }`}
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TimeEditRequestList;
